import {
  isUndefined, max, round, some,
  sumBy, toArray, map, sum,
  isNumber, reduce, includes, filter,
  find,
} from 'lodash';
import { DEFAULT_CURRENCY } from '@sda/utils/lang';
import { ProfileTypes, type Currencies } from '@sda/enums';
import { SIDE_DOOR_FEE_MOD, STRIPE_FEE_PERCENT, STRIPE_TX_FEE } from './lib/constants';
import type { PublicShowProfile } from '@sda/interfaces/show';
import type { CalculatorProps, ProductTotalsList } from './lib/interfaces';
import type { StripeTotals } from '@sda/interfaces/order';
import type { SKUPriceList, CurrentSKUAmountList } from '@sda/interfaces/reducer/stripe.reducer';

export class HyperwalletCalculator {
  private props: CalculatorProps;

  constructor(props: CalculatorProps) {
    this.props = props;

    /* eslint-disable no-constructor-return */
    return new Proxy(this, {
      get: (target, name) => {
        const { stripe, show } = props;
        const { currentSKU, currentSKUAmount } = stripe;

        const cumulativeTypes = [
          'artistTaxTotal', 'artistTotal', 'artistGross',
          'hostTaxTotal', 'hostTotal', 'hostGross',
          'sidedoorTaxTotal', 'sidedoorTotal',
          'netTaxTotal', 'netTaxRate',
          'taxTotal',
          'socanTotal',
          'unitTotal',
          'actualTicketTotal',
        ];

        // value should be cumulative of all products if we receive a full order object with more than one product.
        if (includes(cumulativeTypes, name) && !isNumber(currentSKU?.price) && Object.keys(currentSKU?.price || {}).length > 1) {
          const productCalculators: ProductTotalsList = reduce(currentSKU.price, (results, price, productId) => {
            if (results[productId]) {
              return results;
            }

            const productStripe = {
              ...stripe,
              currentSKU: {
                ...stripe.currentSKU,
                price,
              },
              currentSKUAmount: currentSKUAmount[productId],
            };

            return {
              ...results,
              [productId]: new HyperwalletCalculator({ show, stripe: productStripe }),
            };
          }, {});

          return () => sumBy(Object.values(productCalculators), (calculator) => calculator[name]());
        }

        return Reflect.get(target, name);
      },
    });
  }

  /**
    Profile utility methods
    -----------------------
    Methods for getting payee profiles that support both old and next-gen shows
   */

  /**
   * Returns the artist profile for old shows and the show-owner profile for next-gen shows
   * @return {PublicShowProfile}
   */
  artistProfile(): PublicShowProfile {
    const { hasMultiPartySupport, ownerProfileId, publicProfiles } = this.props.show;

    // Treat the artist as the show owner for next-gen shows
    if (hasMultiPartySupport) {
      const profile = find(publicProfiles, (item) => item.profileId === ownerProfileId);

      return profile;
    }

    const profile = find(publicProfiles, (item) => item.profileType === ProfileTypes.ACT);

    return profile;
  }

  /**
   * Returns the host profile for old shows and the non-show-owner profile for next-gen shows, if available
   * TODO - For true multi-party shows, support for additional payees will need to be added
   * @return {PublicShowProfile}
   */
  hostProfile(): PublicShowProfile {
    const { hasMultiPartySupport, ownerProfileId, publicProfiles } = this.props.show;

    // Treat the artist as the show owner for next-gen shows
    if (hasMultiPartySupport) {
      const profile = find(publicProfiles, (item) => item.profileId !== ownerProfileId);

      return profile;
    }

    const profile = find(publicProfiles, (item) => item.profileType === ProfileTypes.VENUE);

    return profile;
  }

  /*
    PRO Reporting
    -------------
    Online shows - PRO fees are dynamically calculated based on the purchaser's location
    Offline shows - PRO fees are calculated based on the location of the venue
  */

  socanFeePercent(): number {
    const { stripe } = this.props;
    const { currentSKU } = stripe;

    return currentSKU?.proFeePercent || parseFloat(process.env.SOCAN_FEE_PERCENT);
  }

  includeSocanFees(): boolean {
    const { show } = this.props;

    // This is set to default for shows with a musician but has an override in the show builder
    return !!show.hasSocanFee;
  }

  socanTotal(): number {
    // Socan total is always based on the unit (pre-tax) amount
    return this.includeSocanFees() ? Math.round(this.unitTotal() * this.socanFeePercent()) : 0;
  }

  /*
    Processing fee reporting
    ------------------------
    Side Door charges a 150 (local currency) fee per ticket
    We expect the Stripe processing fee to be a worst-case 3.58% + 30 cents
  */

  /* eslint-disable */
  flatStripeFee(): number {
    return parseInt(STRIPE_TX_FEE, 10);
  }
  /* eslint-enable */

  hasPaidProduct(): boolean {
    const { stripe } = this.props;
    const { currentSKU, currentSKUAmount } = stripe;

    if (!currentSKU?.price) {
      return false;
    }

    const prices = this.currentSKUPriceObject(currentSKU.price);
    const currentSKUAmounts = this.currentSKUAmountObject(currentSKUAmount);

    const hasPaidProduct = some(prices, (price, index) => currentSKUAmounts[index] > 0 && price > 0);

    return hasPaidProduct;
  }

  expectedStripeTotal(): number {
    const { stripe } = this.props;
    const { currentSKU } = stripe;

    // Do not anticipate a processing fee for free shows
    if (currentSKU && !this.hasPaidProduct()) {
      return 0;
    }

    // Calculate what we expect the fee to be for the pre-fee total
    // Formula: (Ticket Price + Flat Fee) / (1 - Fee Percent) = Gross Total
    // E.g. (10000 + 30) / ( 1 - 0.0358) = $104.02
    const baseStripeFeePercent = parseFloat(STRIPE_FEE_PERCENT);
    const baseStripeMod = 1 - baseStripeFeePercent;

    const preGross = this.netTaxFeeTotal() + this.flatStripeFee();

    return Math.round(preGross / baseStripeMod) - this.netTaxFeeTotal();
  }

  stripeTotal(): number {
    const { stripe } = this.props;
    const { adjustedStripeTotal, stripeFee } = stripe;

    // If we have the actual, reconciled stripe fee, use that instead of a calculation
    if (!isUndefined(adjustedStripeTotal)) {
      return Math.round(adjustedStripeTotal);
    }

    // Support for stripe fees stored before reconciliation
    if (!isUndefined(stripeFee)) {
      return Math.round(stripeFee);
    }

    return this.expectedStripeTotal();
  }

  sidedoorFee(): number {
    const { stripe } = this.props;
    const {
      adjustedStripeTotal, processingFeeTotal, sidedoorFee, stripeFee,
    } = stripe;

    // Do not include a side door fee for free shows
    if (!this.hasPaidProduct()) {
      return 0;
    }

    if (!isUndefined(adjustedStripeTotal)) {
      return processingFeeTotal - Math.round(adjustedStripeTotal);
    }

    // This field was used before the introduction of side door processing fees
    if (!isUndefined(stripeFee)) {
      return 0;
    }

    if (!isUndefined(sidedoorFee)) {
      return sidedoorFee;
    }

    return (this.totalPaidQuantity() * parseFloat(SIDE_DOOR_FEE_MOD));
  }

  sidedoorFeeTaxTotal(): number {
    const { stripe } = this.props;
    const { sidedoorFeeTaxTotal, sidedoorFee, currentSKU } = stripe;

    if (!isUndefined(sidedoorFeeTaxTotal)) {
      return sidedoorFeeTaxTotal;
    }

    if (!currentSKU?.price) {
      return 0;
    }

    // Flag that can be passed in for orders where we want to skip Side Door's portion of tax collection
    if (currentSKU.skipSideDoorTax) {
      return 0;
    }

    if (isUndefined(sidedoorFeeTaxTotal) && !isUndefined(sidedoorFee)) {
      return 0;
    }

    // Charge the full tax of the show to our processing fee
    return Math.round(this.sidedoorFee() * this.showTaxRate());
  }

  totalFees(): number {
    return this.sidedoorFee() + this.stripeTotal();
  }

  totalExpenses(): number {
    return this.socanTotal();
  }

  currentSKUPriceObject(price: number | SKUPriceList): SKUPriceList {
    if (typeof price === 'number') {
      return { default: price };
    }

    return price;
  }

  currentSKUAmountObject(amount: number | CurrentSKUAmountList): CurrentSKUAmountList {
    if (typeof amount === 'number') {
      return { default: amount };
    }

    return amount;
  }

  /* gross total calculation */

  /**
   * The subtotal is the price of the ticket * quantity
   * For name-your-price shows, this is the price the user entered
   * @return {number}
   */
  subTotal(): number {
    const { stripe } = this.props;
    const { currentSKUAmount, currentSKU } = stripe;

    if (!currentSKU?.price) {
      return 0;
    }

    const currentSKUAmounts = this.currentSKUAmountObject(currentSKUAmount);

    const prices = this.currentSKUPriceObject(currentSKU.price);

    const totals = map(prices, (total, index) => {
      const amount = max([currentSKUAmounts[index], 0]);

      const productSubTotal = total * amount;

      return productSubTotal;
    });

    return sum(totals);
  }

  // The price of the ticket before tax
  // For shows or ticket purchasers outside of US/CA, the tax is built in to the ticket price
  unitTotal(): number {
    if (this.taxIncluded()) {
      return Math.round(this.subTotal() / (1 + this.netTaxRate()));
    }

    return this.subTotal();
  }

  // The actual ticket price
  actualTicketTotal(): number {
    return this.unitTotal() / this.totalQuantity();
  }

  // This is the equivalent of the SPLIT total
  netTotal(): number {
    return max([0, this.unitTotal() - this.socanTotal() - this.supportFeeTotal()]);
  }

  taxTotal(): number {
    if (this.taxIncluded()) {
      return max([0, this.subTotal() - this.unitTotal()]);
    }

    return Math.round(this.subTotal() * this.netTaxRate());
  }

  artistTaxTotal(): number {
    return Math.round(this.unitTotal() * this.artistTaxRate());
  }

  hostTaxTotal(): number {
    return Math.round(this.unitTotal() * this.hostTaxRate());
  }

  artistTotal(): number {
    return Math.round(this.netTotal() * this.artistSplitPercent());
  }

  hostTotal(): number {
    return Math.round(this.netTotal() * this.hostSplitPercent());
  }

  sidedoorTotal(): number {
    return max([0, this.netTotal() - this.artistTotal() - this.hostTotal()]);
  }

  artistGross(): number {
    return this.artistTotal() + this.artistTaxTotal();
  }

  hostGross(): number {
    return this.hostTotal() + this.hostTaxTotal();
  }

  /**
   * This is the canonical Side Door gross, but the actual amount represented in the front end is:
   * this.sidedoorTotal() + this.sidedoorTaxtotal()
   * @return {number}
   */
  sidedoorGross(): number {
    return max([
      0,
      this.sidedoorTotal()
      + this.sidedoorTaxTotal()
      + this.socanTotal()
      + this.sidedoorFee()
      + this.sidedoorFeeTaxTotal()
      + this.supportFeeTotal(),
    ]);
  }

  netTaxTotal(): number {
    return Math.round(this.unitTotal() + this.taxTotal());
  }

  netTaxFeeTotal(): number {
    return Math.round(this.unitTotal() + this.taxTotal() + this.sidedoorFee() + this.sidedoorFeeTaxTotal());
  }

  grossTotal(): number {
    return Math.round(this.netTaxFeeTotal() + this.stripeTotal());
  }

  /**
   * In order to reconcile possible rounding errors, Side Door takes whatever is left over
   * @return {number}
   */
  sidedoorTaxTotal(): number {
    return this.taxTotal() - this.artistTaxTotal() - this.hostTaxTotal();
  }

  /* tax utility methods */

  netTaxRate(): number {
    return round(this.artistTaxRate() + this.hostTaxRate() + this.sidedoorTaxRate(), 4);
  }

  sidedoorSplitPercent(): number {
    const { show } = this.props;

    return show.sidedoorSplitPercent || 0;
  }

  hostSplitPercent(): number {
    const profile = this.hostProfile();

    return profile?.profileSplitPercent || 0;
  }

  artistSplitPercent(): number {
    const profile = this.artistProfile();

    return profile?.profileSplitPercent || 0;
  }

  artistCollectsTax(): boolean {
    const profile = this.artistProfile();

    return profile?.profileCollectsTax || false;
  }

  hostCollectsTax(): boolean {
    const profile = this.hostProfile();

    return profile?.profileCollectsTax || false;
  }

  artistTaxRate(): number {
    return (this.artistCollectsTax()) ? this.showTaxRate() * this.artistSplitPercent() : 0;
  }

  hostTaxRate(): number {
    return (this.hostCollectsTax()) ? this.showTaxRate() * this.hostSplitPercent() : 0;
  }

  sidedoorTaxRate(): number {
    const { stripe } = this.props;
    const { currentSKU } = stripe;

    if (!currentSKU?.price) {
      return 0;
    }

    // Flag that can be passed in for orders where we want to skip Side Door's portion of tax collection
    if (currentSKU.skipSideDoorTax) {
      return 0;
    }

    return this.showTaxRate() * this.sidedoorSplitPercent();
  }

  /**
   * Online tickets: the tax rate is set to the location of the purchaser
   * Offline tickets: the tax rate is set to the location of the venue
   * @return {number}
   */
  showTaxRate(): number {
    const { show } = this.props;

    return show?.taxRate || 0;
  }

  /* utility methods */

  currency(): Currencies {
    const { stripe } = this.props;
    const { currentSKU } = stripe;

    if (!currentSKU?.currency) {
      return DEFAULT_CURRENCY;
    }

    return currentSKU?.currency || DEFAULT_CURRENCY;
  }

  /**
   * Determines whether or not tax should be included in the ticket price, or if it's on top
   * Online Shows: Tax is included in the ticket price if the purchaser is outside of US/CA
   * Offline Shows: Tax is included in the ticket price if the location of the show is outside of US/CA
   * @return {boolean}
   */
  taxIncluded(): boolean {
    const { stripe } = this.props;
    const { currentSKU } = stripe;

    return currentSKU?.taxIncluded || false;
  }

  /**
   * Price to customer for a single ticket of each type
   */
  customerTotal(): number {
    const { stripe } = this.props;
    const { currentSKU } = stripe;

    if (!currentSKU?.price) {
      return 0;
    }

    const prices = this.currentSKUPriceObject(currentSKU.price);

    const totals = map(prices, (total) => total);

    return sum(totals);
  }

  /**
   * The adjusted price of a single ticket
   * If multiple ticket types are in the same order this number is an average
   * Note that multiplying this by the amount may not result in the same number as this.unitTotal due to rounding
   * @return {number}
   */
  originalTicketPrice(): number {
    const { stripe } = this.props;
    const { currentSKU, currentSKUAmount } = stripe;

    if (!currentSKU?.price) {
      return 0;
    }

    const { taxIncluded, price } = currentSKU;

    const prices = this.currentSKUPriceObject(price);
    const amounts = this.currentSKUAmountObject(currentSKUAmount);

    let finalPrice = 0;

    // If there is more than 1 product average the prices.
    if (Object.keys(prices).length > 1) {
      const ticketsInOrder = sumBy(Object.values(amounts), (qty) => qty);
      const totalPrice = sumBy(Object.values(prices), (productPrice) => productPrice);

      finalPrice = totalPrice / ticketsInOrder;
    } else {
      [finalPrice] = Object.values(prices);
    }

    if (taxIncluded) {
      return Math.round(finalPrice / (1 + this.netTaxRate()));
    }

    return Math.round(finalPrice);
  }

  supportFeeTotal(): number {
    return 0;
  }

  totalPaidQuantity(): number {
    const { stripe } = this.props;
    const { currentSKUAmount, currentSKU } = stripe;

    if (!currentSKU?.price) {
      return 0;
    }

    const currentSKUAmounts = this.currentSKUAmountObject(currentSKUAmount);
    const prices = this.currentSKUPriceObject(currentSKU.price);

    const filteredSKUAmounts = filter(currentSKUAmounts, (qty, index) => qty && prices[index] > 0);

    const total = sumBy(toArray(filteredSKUAmounts), (qty) => qty);

    return total;
  }

  totalQuantity(): number {
    const { stripe } = this.props;
    const { currentSKUAmount } = stripe;

    const currentSKUAmounts = this.currentSKUAmountObject(currentSKUAmount);

    const total = sumBy(toArray(currentSKUAmounts), (qty) => qty);

    return total;
  }

  setStripeTotals(stripeTotals: StripeTotals): void {
    const { stripe } = this.props;

    this.props = {
      ...this.props,
      stripe: {
        ...stripe,
        ...stripeTotals,
      },
    };
  }

  getTotalForId(profileId: string): number {
    const { publicProfiles, ownerProfileId, hasMultiPartySupport } = this.props.show;

    if (hasMultiPartySupport) {
      if (profileId === ownerProfileId) {
        return this.artistTotal();
      }

      return this.hostTotal();
    }

    const profile = publicProfiles.find((profileData) => profileData.profileId === profileId);

    if (profile?.profileType === ProfileTypes.ACT) {
      return this.artistTotal();
    }

    return this.hostTotal();
  }
}
